import { IamClient } from "iam-client";
import React, { useState } from "react";
import {
  LanguageFrontendType,
  LanguageCode,
} from "../pages/shared/components/language/types";
import { Params } from "../api/commercial/url/params";

const defaultLanguage: LanguageFrontendType = LanguageFrontendType.de;

const LANGUAGE_KEY = "commercial.onboarding.language";

export interface LanguageContextType {
  language: LanguageFrontendType;
  setLanguage: (userProvidedLanguage: LanguageFrontendType) => void;
}

/**
 *
 * Translates the short language code into a long language code.
 *
 * @param language The language in a short format.
 * @return String The language in a full format.
 */
const toFullLanguageKey = (language: LanguageCode): LanguageFrontendType => {
  switch (language) {
    case LanguageCode.de:
      return LanguageFrontendType.de;
    case LanguageCode.en:
      return LanguageFrontendType.en;
    case LanguageCode.fr:
      return LanguageFrontendType.fr;
    case LanguageCode.it:
      return LanguageFrontendType.it;
    default:
      return LanguageFrontendType.de;
  }
};

/**
 *
 * Translates the full language code into a short language code.
 *
 * @param language The language in a full format.
 * @return String The language in a short format.
 */
const toShortLanguageKey = (language: LanguageFrontendType): LanguageCode => {
  switch (language) {
    case LanguageFrontendType.de:
      return LanguageCode.de;
    case LanguageFrontendType.en:
      return LanguageCode.en;
    case LanguageFrontendType.fr:
      return LanguageCode.fr;
    case LanguageFrontendType.it:
      return LanguageCode.it;
    default:
      return LanguageCode.de;
  }
};

const persistLanguage = (language: LanguageFrontendType) => {
  localStorage.setItem(LANGUAGE_KEY, language);
};

export const getPersistedLanguage = (): LanguageFrontendType | null =>
  localStorage.getItem(LANGUAGE_KEY) as LanguageFrontendType;

const getInitialLanguage = (): LanguageFrontendType => {
  const urlParameter = Params.language.get();
  const languageFromUrl = urlParameter ? toFullLanguageKey(urlParameter) : null;
  const languageAsString = localStorage.getItem(LANGUAGE_KEY);
  const languageFromLocalStorage: LanguageFrontendType =
    (languageAsString as LanguageFrontendType) ?? defaultLanguage;

  if (languageFromUrl && languageFromUrl !== languageFromLocalStorage) {
    persistLanguage(languageFromUrl);
  }

  const language = languageFromUrl || languageFromLocalStorage;
  IamClient.setLanguage(toShortLanguageKey(language));

  return language;
};

const initialLanguage = getInitialLanguage();
export const LanguageContext = React.createContext({} as LanguageContextType);

interface LanguageProviderProps {
  children: React.ReactNode;
}

export default function LanguageProvider({
  children,
}: LanguageProviderProps): React.JSX.Element {
  const [language, setLanguageState] = useState(initialLanguage);

  const setLanguage = (userProvidedLanguage: LanguageFrontendType) => {
    persistLanguage(userProvidedLanguage);
    IamClient.setLanguage(toShortLanguageKey(userProvidedLanguage));
    setLanguageState(userProvidedLanguage);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}
